import axios, { AxiosError } from 'axios'
import { apiBaseUrl, apiTimeout } from '@/config/environment'

// Create axios instance with optimized settings
export const api = axios.create({
  baseURL: apiBaseUrl,
  timeout: apiTimeout,
  headers: {
    'Content-Type': 'application/json',
  },
  // Optimize for better performance
  maxRedirects: 5,
  maxContentLength: 50 * 1024 * 1024, // 50MB
  validateStatus: (status) => {
    return status >= 200 && status < 300; // Accept 2xx status codes
  },
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    
    return config
  },
  (error) => {
    console.error('Axios request interceptor error:', error)
    return Promise.reject(error)
  }
)


// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    // Handle common errors
    if (error.response?.status === 401) {
      // Unauthorized - log but don't clear token automatically
      // Let the calling code handle token cleanup
      console.warn('Unauthorized access detected')
    } else if (error.response?.status === 403) {
      // Forbidden
      console.error('Access forbidden')
    } else if (error.response?.status === 429) {
      // Rate limit exceeded
      const retryAfter = error.response.headers['retry-after'] || 60
      console.warn(`Rate limit exceeded. Retry after ${retryAfter} seconds`)
      
      // You could implement retry logic here
      // setTimeout(() => retryRequest(error.config), retryAfter * 1000)
    } else if (error.response?.status >= 500) {
      // Server error
      console.error('Server error:', error.response?.data)
    }
    
    return Promise.reject(error)
  }
)

// API helper functions
export const apiService = {
  // Generic CRUD operations
  get: (url: string, params?: any) => api.get(url, { params }),
  post: (url: string, data?: any) => api.post(url, data),
  put: (url: string, data?: any) => api.put(url, data),
  patch: (url: string, data?: any) => api.patch(url, data),
  delete: (url: string) => api.delete(url),

  // Campaigns
  campaigns: {
    getAll: (params?: any) => api.get('/campaigns', { params }),
    getById: (id: number) => api.get(`/campaigns/${id}`),
    create: (data: any) => api.post('/campaigns', data),
    update: (id: number, data: any) => api.put(`/campaigns/${id}`, data),

    updateStatus: (id: number, statusId: number) => api.patch(`/campaigns/${id}/status`, { status_id: statusId }),
    getStats: (params?: any) => api.get('/campaigns/stats', { params }),
  },

  // Templates
  templates: {
    getAll: (params?: any) => api.get('/templates', { params }),
    getById: (id: number) => api.get(`/templates/${id}`),
    create: (data: any) => api.post('/templates', data),
    update: (id: number, data: any) => api.put(`/templates/${id}`, data),
    delete: (id: number) => api.delete(`/templates/${id}`),
    updateStatus: (id: number, status: string) => api.patch(`/templates/${id}/status`, { status }),
  },

  // Categories
  categories: {
    getAll: (params?: any) => api.get('/categories', { params }),
    getById: (id: number) => api.get(`/categories/${id}`),
    create: (data: any) => api.post('/categories', data),
    update: (id: number, data: any) => api.put(`/categories/${id}`, data),
    delete: (id: number) => api.delete(`/categories/${id}`),
    getSubcategories: (categoryId: number, params?: any) => api.get(`/categories/${categoryId}/subcategories`, { params }),
  },

  // Payment Methods
  paymentMethods: {
    getAll: () => api.get('/payment-methods'),
    getForDropdown: () => api.get('/payment-methods/dropdown'),
  },

  // Account Payment Methods
  accountPaymentMethods: {
    getByAccount: (accountId: number) => api.get(`/accounts/${accountId}/payment-methods`),
    addToAccount: (accountId: number, data: any) => api.post(`/accounts/${accountId}/payment-methods`, data),
    updatePriority: (id: number, priority: number) => api.put(`/accounts/payment-methods/${id}/priority`, { priority }),
    toggleStatus: (id: number) => api.patch(`/accounts/payment-methods/${id}/status`),
    removeFromAccount: (id: number) => api.delete(`/accounts/payment-methods/${id}`),
  },

  // Advertiser Payment Methods
  advertiserPaymentMethods: {
    getByAdvertiser: (advertiserId: number) => api.get(`/advertisers/${advertiserId}/payment-methods`),
    getByAccount: (accountId: number) => api.get(`/accounts/${accountId}/advertiser-payment-methods`),
    getByAccountAndAdvertiser: (accountId: number, advertiserId: number) => api.get(`/accounts/${accountId}/advertisers/${advertiserId}/payment-methods`),
    addToAdvertiser: (accountId: number, advertiserId: number, data: any) => api.post(`/accounts/${accountId}/advertisers/${advertiserId}/payment-methods`, data),
    updatePriority: (id: number, priority: number) => api.put(`/advertiser-payment-methods/${id}/priority`, { priority }),
    updateLimit: (id: number, limit: number | null) => api.put(`/advertiser-payment-methods/${id}/limit`, { limit }),
    removeFromAdvertiser: (id: number) => api.delete(`/advertiser-payment-methods/${id}`),
  },

  // Advertisers
  advertisers: {
    getAll: (params?: any) => api.get('/advertisers', { params }),
    getById: (id: number) => api.get(`/advertisers/${id}`),
    create: (data: any) => api.post('/advertisers', data),
    update: (id: number, data: any) => api.put(`/advertisers/${id}`, data),
    delete: (id: number) => api.delete(`/advertisers/${id}`),
    search: (term: string, params?: any) => api.get(`/advertisers/search/${term}`, { params }),
    getByAccount: (accountId: number) => api.get(`/accounts/${accountId}/advertisers`),
  },

  // Accounts
  accounts: {
    getAll: (params?: any) => api.get('/accounts', { params }),
    getById: (id: number) => api.get(`/accounts/${id}`),
    create: (data: any) => api.post('/accounts', data),
    update: (id: number, data: any) => api.put(`/accounts/${id}`, data),
    delete: (id: number) => api.delete(`/accounts/${id}`),
    getCommercialAdmins: (accountId: number) => api.get(`/accounts/${accountId}/commercial-admins`),
    addCommercialAdmin: (accountId: number, userId: number) => api.post(`/accounts/${accountId}/commercial-admins`, { user_id: userId }),
    removeCommercialAdmin: (accountId: number, userId: number) => api.delete(`/accounts/${accountId}/commercial-admins/${userId}`),
    getOperationalAdmins: (accountId: number) => api.get(`/accounts/${accountId}/operational-admins`),
    addOperationalAdmin: (accountId: number, userId: number) => api.post(`/accounts/${accountId}/operational-admins`, { user_id: userId }),
    removeOperationalAdmin: (accountId: number, userId: number) => api.delete(`/accounts/${accountId}/operational-admins/${userId}`),
    getPaymentMethods: (accountId: number) => api.get(`/accounts/${accountId}/payment-methods`),
    addPaymentMethod: (accountId: number, data: any) => api.post(`/accounts/${accountId}/payment-methods`, data),
    updatePaymentMethod: (id: number, data: any) => api.put(`/accounts/payment-methods/${id}`, data),
    removePaymentMethod: (id: number) => api.delete(`/accounts/payment-methods/${id}`),
    getScenarioTypes: (accountId: number) => api.get(`/accounts/${accountId}/scenario-types`),
    addScenarioType: (accountId: number, data: any) => api.post(`/accounts/${accountId}/scenario-types`, data),
    updateScenarioType: (accountId: number, scenarioTypeId: number, data: any) => api.put(`/accounts/${accountId}/scenario-types/${scenarioTypeId}`, data),
    removeScenarioType: (accountId: number, scenarioTypeId: number) => api.delete(`/accounts/${accountId}/scenario-types/${scenarioTypeId}`),
    
    // User Profiles
    getUserProfiles: (accountId: number) => api.get(`/accounts/${accountId}/user-profiles`),
    getAvailableUsers: (accountId: number) => api.get(`/accounts/${accountId}/available-users`),
    addUserProfiles: (accountId: number, data: any) => api.post(`/accounts/${accountId}/user-profiles`, data),
    removeUserProfile: (accountId: number, userProfileId: number) => api.delete(`/accounts/${accountId}/user-profiles/${userProfileId}`),
    removeUserFromAccount: (accountId: number, userId: number) => api.delete(`/accounts/${accountId}/users/${userId}`),
    
    // Campaign Variables
    getCampaignVariables: (accountId: number, accountScenarioTypeId: number, params?: any) =>
      api.get(`/accounts/${accountId}/scenario-types/${accountScenarioTypeId}/campaign-variables`, { params }),
    addCampaignVariable: (accountId: number, accountScenarioTypeId: number, data: any) => api.post(`/accounts/${accountId}/scenario-types/${accountScenarioTypeId}/campaign-variables`, data),
    updateCampaignVariable: (accountId: number, accountScenarioTypeId: number, variableId: number, data: any) => api.put(`/accounts/${accountId}/scenario-types/${accountScenarioTypeId}/campaign-variables/${variableId}`, data),
    deleteCampaignVariable: (accountId: number, accountScenarioTypeId: number, variableId: number) => api.delete(`/accounts/${accountId}/scenario-types/${accountScenarioTypeId}/campaign-variables/${variableId}`),
    
    // Event Variables
    getEventVariables: (accountId: number, accountScenarioTypeId: number, params?: any) =>
      api.get(`/accounts/${accountId}/scenario-types/${accountScenarioTypeId}/event-variables`, { params }),
    addEventVariable: (accountId: number, accountScenarioTypeId: number, data: any) => api.post(`/accounts/${accountId}/scenario-types/${accountScenarioTypeId}/event-variables`, data),
    updateEventVariable: (accountId: number, accountScenarioTypeId: number, variableId: number, data: any) => api.put(`/accounts/${accountId}/scenario-types/${accountScenarioTypeId}/event-variables/${variableId}`, data),
    deleteEventVariable: (accountId: number, accountScenarioTypeId: number, variableId: number) => api.delete(`/accounts/${accountId}/scenario-types/${accountScenarioTypeId}/event-variables/${variableId}`),

    // Account campaign categories
    getCampaignCategories: (accountId: number) => api.get(`/accounts/${accountId}/categories`),
    toggleCampaignCategory: (accountId: number, data: any) => api.post(`/accounts/${accountId}/categories/toggle`, data),
    
    // Account services
    getServices: (accountId: number) => api.get(`/accounts/${accountId}/services`),
  },

  // Users
  users: {
    getAll: (params?: any) => api.get('/users', { params }),
    getAvailableForAccount: (accountId: number, type?: 'commercial' | 'operational') => api.get(`/users/available/${accountId}`, { params: { type } }),
    getById: (id: number) => api.get(`/users/${id}`),
    create: (data: any) => api.post('/users', data),
    update: (id: number, data: any) => api.put(`/users/${id}`, data),
    delete: (id: number) => api.delete(`/users/${id}`),
    getProfiles: (userId: number) => api.get(`/users/${userId}/profiles`),
    assignProfile: (userId: number, data: any) => api.post(`/users/${userId}/profiles`, data),
    updateProfile: (userId: number, accountId: number, data: any) => api.put(`/users/${userId}/profiles/${accountId}`, data),
    removeProfile: (userId: number, accountId: number) => api.delete(`/users/${userId}/profiles/${accountId}`),
  },

  // Profiles
  profiles: {
    getAll: (params?: any) => api.get('/profiles', { params }),
    getById: (id: number) => api.get(`/profiles/${id}`),
    create: (data: any) => api.post('/profiles', data),
    update: (id: number, data: any) => api.put(`/profiles/${id}`, data),
    delete: (id: number) => api.delete(`/profiles/${id}`),
    getProfilePrivileges: (profileId: number) => api.get(`/profiles/${profileId}/privileges`),
    getProfilePrivilegesCatalog: (profileId: number, params?: any) => api.get(`/profiles/${profileId}/privileges/catalog`, { params }),
    updateProfilePrivileges: (profileId: number, data: any) => api.put(`/profiles/${profileId}/privileges`, data),
    updateProfilePrivilege: (profileId: number, privilegeId: number, data: any) => api.patch(`/profiles/${profileId}/privileges/${privilegeId}`, data),
  },

  // Privileges
  privileges: {
    getAll: (params?: any) => api.get('/privileges', { params }),
    getById: (id: number) => api.get(`/privileges/${id}`),
    create: (data: any) => api.post('/privileges', data),
    update: (id: number, data: any) => api.put(`/privileges/${id}`, data),
    delete: (id: number) => api.delete(`/privileges/${id}`),
  },

  // Operators
  operators: {
    getAll: (params?: any) => api.get('/operators', { params }),
    getById: (id: number) => api.get(`/operators/${id}`),
    create: (data: any) => api.post('/operators', data),
    update: (id: number, data: any) => api.put(`/operators/${id}`, data),
    delete: (id: number) => api.delete(`/operators/${id}`),
  },

  // Countries
  countries: {
    getAll: (params?: any) => api.get('/countries', { params }),
    getById: (id: number) => api.get(`/countries/${id}`),
    create: (data: any) => api.post('/countries', data),
    update: (id: number, data: any) => api.put(`/countries/${id}`, data),
    delete: (id: number) => api.delete(`/countries/${id}`),
  },

  // Scenario Types
  scenarioTypes: {
    getAll: (params?: any) => api.get('/scenario-types', { params }),
    getAvailableForAccount: (accountId: number) => api.get(`/scenario-types/available/${accountId}`),
    getById: (id: number) => api.get(`/scenario-types/${id}`),
    create: (data: any) => api.post('/scenario-types', data),
    update: (id: number, data: any) => api.put(`/scenario-types/${id}`, data),
    delete: (id: number) => api.delete(`/scenario-types/${id}`),
    getVariables: (scenarioTypeId: number) => api.get(`/scenario-types/${scenarioTypeId}/variables`),
    updateVariables: (scenarioTypeId: number, data: any) => api.put(`/scenario-types/${scenarioTypeId}/variables`, data),
  },

  // Variables
  variables: {
    getAll: (params?: any) => api.get('/variables', { params }),
    getAvailableForAccount: (accountId: number) => api.get(`/variables/available/${accountId}`),
    getById: (id: number) => api.get(`/variables/${id}`),
    create: (data: any) => api.post('/variables', data),
    update: (id: number, data: any) => api.put(`/variables/${id}`, data),
    delete: (id: number) => api.delete(`/variables/${id}`),
  },

  // Channels
  channels: {
    getAll: (params?: any) => api.get('/channels', { params }),
    getScenarioTypesByChannel: (channelId: number) => api.get(`/channels/${channelId}/scenario-types`),
  },

  // Subcategories
  subcategories: {
    getAll: (params?: any) => api.get('/subcategories', { params }),
    getById: (id: number) => api.get(`/subcategories/${id}`),
    getByCategory: (categoryId: number) => api.get(`/subcategories/category/${categoryId}`),
    create: (data: any) => api.post('/subcategories', data),
    update: (id: number, data: any) => api.put(`/subcategories/${id}`, data),
    delete: (id: number) => api.delete(`/subcategories/${id}`),
  },

  // Tools
  tools: {
    getAvailable: () => api.get('/tools/available'),
    executeCampaignMigration: (data: { campaign_id: number; new_bookmark_id: number; commercial_model_id?: number; price?: number }) => 
      api.post('/tools/campaign-migration', data),
    executeMovistarOcsJob: (data?: { blockNotifications?: boolean }) =>
      api.post('/tools/movistar-ocs-job', data ?? {}),
    executeRunFullSegmentation: (data: { runCreateCampaigns?: boolean; skipRunFull?: boolean }) =>
      api.post('/tools/run-full-segmentation', data),
    uploadAudience: (formData: FormData) =>
      api.post('/tools/upload-audience', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    executeRunFullSegmentationPostpaid: (data: { accountId: number; month?: string; audienceKey: string; runCreateCampaigns?: boolean }) =>
      api.post('/tools/run-full-segmentation-postpaid', data),
    executeCellcPaidSegmentationSimulate: (data: { accountId?: number; date?: string; audienceKey?: string; manualServiceIds?: number[]; runId?: string }) =>
      api.post('/tools/cellc-paid-segmentation/simulate', data),
    executeCellcPaidSegmentationConfirm: (data: { accountId?: number; runId: string }) =>
      api.post('/tools/cellc-paid-segmentation/confirm', data),
    getCellcSegmentationRuns: (accountId?: number) =>
      api.get('/tools/cellc-segmentation/runs', { params: accountId !== undefined ? { accountId } : {} }),
    getCellcSegmentationRunFiles: (runId: string) =>
      api.get(`/tools/cellc-segmentation/runs/${encodeURIComponent(runId)}/files`),
    getGeneratedListDays: (accountId: number) =>
      api.get('/tools/generated-list-days', { params: { accountId } }),
    getGeneratedListHash: (accountId: number, date: string) =>
      api.get('/tools/generated-list-hash', { params: { accountId, date } }),
    executeCreateCampaignsFromFiles: (data: { accountId: number; zipBucket: string; zipKey: string; csvFiles: Array<{ serviceId: number; fileName: string; sourceList: string }> }) =>
      api.post('/tools/create-campaigns-from-files', data),
    getEntelStats: (month: string, accountId: number) =>
      api.get('/tools/entel-stats', { params: { month, accountId } }),
    saveEntelStats: (month: string, rows: any[], accountId: number) =>
      api.post('/tools/entel-stats', { month, rows, accountId }),
  },
}

export default api
